import { useState } from 'react';
import { api } from '../../lib/api';
import { SectionShell } from './SectionShell';
import type { SectionStatus, TenantSectionProps } from './types';

export type BotMode = 'informational' | 'transactional' | 'hybrid';

interface ModeOption {
  id: BotMode;
  title: string;
  description: string;
  tools: string[];
  suggestedTier: string;
}

const MODES: ModeOption[] = [
  {
    id: 'informational',
    title: 'Informativo',
    description:
      'El bot responde consultas sobre el negocio (servicios, horarios, FAQs). No realiza acciones en el ERP.',
    tools: ['searchKnowledge', 'escalateToHuman'],
    suggestedTier: 'Base',
  },
  {
    id: 'transactional',
    title: 'Transaccional',
    description:
      'El bot realiza acciones: consultar disponibilidad, agendar y cancelar citas. Requiere ERP configurado.',
    tools: ['getAvailability', 'bookAppointment', 'cancelAppointment', 'escalateToHuman'],
    suggestedTier: 'Pro',
  },
  {
    id: 'hybrid',
    title: 'Híbrido',
    description: 'Combina informativo + transaccional. Recomendado para la mayoría de negocios.',
    tools: [
      'searchKnowledge',
      'getAvailability',
      'bookAppointment',
      'cancelAppointment',
      'escalateToHuman',
    ],
    suggestedTier: 'Pro / Premium',
  },
];

export function botModeStatus(value: BotMode | null | undefined): SectionStatus {
  return value ? 'complete' : 'empty';
}

export function BotModeSection({
  mode,
  tenantId,
  value,
  onSaved,
  testId,
}: TenantSectionProps<BotMode | null | undefined>) {
  const [selected, setSelected] = useState<BotMode | null>(value ?? null);
  const [savedMode, setSavedMode] = useState<BotMode | null>(value ?? null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (!selected) {
      setError('Elegí un modo antes de guardar');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await api.tenants.setBotMode(tenantId, selected);
      setSavedMode(selected);
      onSaved?.(selected);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <SectionShell
      mode={mode}
      testId={testId}
      title="Modo del bot"
      description="Qué puede hacer el bot por su cuenta. Se puede cambiar cuando quieras."
      status={mode === 'edit' && !savedMode ? 'unknown' : botModeStatus(savedMode)}
      error={error}
      saving={saving}
      saved={savedMode !== null}
      saveDisabled={!selected}
      onSave={save}
    >
      {mode === 'edit' && (
        <p className="text-body-sm text-neutral-500 dark:text-neutral-400">
          La API no devuelve todavía el modo guardado: elegí uno y guardá para fijarlo.
        </p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {MODES.map((m) => (
          <button
            key={m.id}
            type="button"
            data-testid={`mode-card-${m.id}`}
            aria-pressed={selected === m.id}
            onClick={() => setSelected(m.id)}
            className={`p-4 rounded-md border-2 text-left transition cursor-pointer ${
              selected === m.id
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                : 'border-neutral-200 bg-surface-base hover:border-neutral-400 dark:border-neutral-700'
            }`}
          >
            <h3 className="font-semibold text-neutral-800 dark:text-neutral-100 mb-2">{m.title}</h3>
            <p className="text-sm text-neutral-600 dark:text-neutral-300 mb-3">{m.description}</p>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-1">
              <strong>Herramientas:</strong> {m.tools.join(', ')}
            </p>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              <strong>Tier sugerido:</strong> {m.suggestedTier}
            </p>
          </button>
        ))}
      </div>
    </SectionShell>
  );
}
